<template>
	<div>
		<div class="alert alert-info">
			<p class="mb-0">Would you like to edit the settings for {{ team.name }}?</p>
		</div>
		<div class="row">
			<div class="col-md-6">
				<div class="row align-items-center justify-content-center mb-3">
					<div class="col-md-4">
						<strong>Trial Date</strong>
					</div>
					<div class="col-md-8">
						<date-picker @date-picker-updated="changeTrialDate" />
					</div>
				</div>
				<div class="row align-items-center justify-content-center mb-3">
					<div class="col-md-4">
						<strong>Property Type</strong>
					</div>
					<div class="col-md-8">
						<select
							class="form-select form-control"
							v-model="propertyType"
							@change="() => (this.propertyTypeChanged = true)"
						>
							<option value="hotel">Hotel</option>
							<option value="restaurant">Restaurant</option>
							<option value="cruiseline">Cruiseline</option>
							<option value="bank">Bank</option>
						</select>
					</div>
				</div>
				<div class="row align-items-center justify-content-center mb-3">
					<div class="col-md-4">
						<strong>Number of competitors</strong>
					</div>
					<div class="col-md-8">
						<input
							type="number"
							class="form-control"
							v-model="newNumberOfCompetitors"
						/>
					</div>
				</div>
				<div class="row align-items-center justify-content-center mb-3">
					<div class="col-md-4">
						<strong>Features</strong>
					</div>
					<div class="col-md-8">
						<div class="custom-control custom-switch mt-5 mb-5">
							<input
								type="checkbox"
								v-model="changeFeatures"
								class="custom-control-input"
								id="customSwitches"
							/>
							<label class="custom-control-label" for="customSwitches"
								>Do you want to change the features?</label
							>
						</div>
						<div v-if="changeFeatures">
							<div v-for="feature in featureList" :key="feature.id" class="mb-1">
								<div class="d-flex align-items-center">
									<label class="mb-0 mr-2" :for="feature.id">{{
										feature.label
									}}</label>
									<input
										:id="feature.id"
										type="checkbox"
										v-model="features[feature.key].enabled"
									/>

									<select
										v-if="feature.hasSelect && features[feature.key].enabled"
										v-model="features[feature.key].feature_data"
										class="ml-3"
									>
										<option
											v-for="(language, key) in languages"
											:key="key"
											:value="key"
										>
											{{ language }}
										</option>
									</select>
								</div>

								<small v-if="features[feature.key]?.created_at" class="text-muted">
									Activation Date: {{ features[feature.key]?.created_at }}
								</small>
							</div>
						</div>
					</div>
				</div>
				<div class="text-right">
					<button class="btn btn-primary" :disabled="isSaving" @click="updateTrial">
						Update
					</button>
				</div>
			</div>
			<div class="col-md-3">
				<strong>Team Groups</strong>
				<div v-if="team.groups && team.groups.length > 0">
					<ul class="list-unstyled">
						<li
							class="py-2 d-flex justify-content-between align-items-center"
							v-for="group in team.groups"
							:key="group.id"
						>
							<span>{{ group.name }}</span>
							<a
								class="btn btn-secondary"
								href="#"
								@click.prevent="removeTeamGroup(group.id)"
								><i class="fas fa-times"></i> Remove</a
							>
						</li>
					</ul>
				</div>
			</div>

			<div v-if="!partner" class="col-md-3">
				<strong>Competition</strong>
				<ul class="list-unstyled">
					<li
						class="py-2 d-flex justify-content-between align-items-center"
						v-for="competitor in teamMetrics.competitors"
						:key="competitor.id"
					>
						<span>{{ competitor.name }}</span>
						<a
							class="btn btn-secondary"
							href="#"
							@click.prevent="deleteCompetitor(competitor)"
							><i class="fas fa-times"></i> Remove</a
						>
					</li>
				</ul>
			</div>
		</div>
		<div class="row mt-3">
			<div v-if="teamConnections !== null" class="col-md-6">
				<strong class="mb-3">Manage Connections</strong>
				<table class="table">
					<tr>
						<th>Connection</th>
						<th>ID</th>
						<th></th>
					</tr>
					<tr v-for="connection in teamConnections" :key="connection.id">
						<td class="align-middle">{{ connection.connection_type }}</td>
						<td class="align-middle">{{ connection.connection_id }}</td>
						<td class="align-middle">
							<button
								class="btn btn-default btn-rounded"
								@click="removeConnection(connection)"
							>
								<i class="fa fa-times"></i>
							</button>
							<button
								v-if="connection.connection_type === 'email'"
								class="btn btn-default btn-rounded ml-2"
								@click="editConnection(connection)"
							>
								<i class="fa fa-edit"></i>
							</button>
						</td>
					</tr>
					<tr>
						<td colspan="3">
							<div class="py-3 d-flex align-items-center justify-content-start">
								<select class="mr-2" v-model="newConnectionType">
									<option value="exclusivi">Exclusivi</option>
									<option value="vertical_booking">Vertical Booking</option>
									<option value="hotel_feedback">HotelFeedback</option>
									<option value="expedia">Expedia Reviews</option>
									<option value="email">Custom PMS Connection</option>
								</select>
								<input
									v-if="newConnectionType !== 'email'"
									class="form-control mr-2"
									type="text"
									v-model="newConnectionId"
								/>
								<button class="btn btn-primary" @click="addConnection">
									{{ newConnectionType === 'email' ? 'Create' : 'Add' }}
								</button>
							</div>
						</td>
					</tr>
				</table>
			</div>
		</div>
		<AdminPmsConnection
			:teams="teams"
			:team="team"
			:show="showPmsEmailModal"
			:teamConnections="teamConnections"
			@modal-closed="showPmsEmailModal = false"
		>
		</AdminPmsConnection>
	</div>
</template>

<script>
import moment from 'moment';
import AdministrationMixin from '../AdministrationMixin';
import SurveysService from '../../../services/surveys-service';
const allowedFeatures = [
	'surveys',
	'cases',
	// 'publish_reviews',
	// 'social_feed',
	'auto_translate',
	'auto_translate_responses',
	'response_approval',
];
import AdminPmsConnection from '../AdminPmsConnection.vue';
export default {
	components: {
		AdminPmsConnection,
	},
	props: {
		team: Object,
		teams: Array,
		teamMetrics: Object,
		partner: {
			type: Object,
			default: null,
		},
	},
	mixins: [AdministrationMixin],
	data() {
		return {
			newTrialDate: null,
			isSaving: false,
			newNumberOfCompetitors: 0,
			propertyType: '',
			propertyTypeChanged: false,
			changeFeatures: false,
			features: {
				surveys: { enabled: false, feature_data: null, created_at: null },
				// publish_reviews: { enabled: false, feature_data: null, created_at: null },
				// social_feed: { enabled: false, feature_data: null, created_at: null },
				auto_translate: { enabled: false, feature_data: 'en', created_at: null },
				auto_translate_responses: { enabled: false, feature_data: 'en', created_at: null },
				response_approval: { enabled: false, feature_data: null, created_at: null },
			},
			featureList: [
				{ id: 'surveys', label: 'Surveys', key: 'surveys', hasSelect: false },
				// { id: 'publishReviews', label: 'Publish Reviews', key: 'publish_reviews', hasSelect: false },
				// { id: 'socialFeed', label: 'Social Feed', key: 'social_feed', hasSelect: false },
				{ id: 'autoTranslate', label: 'Auto Translate', key: 'auto_translate', hasSelect: true },
				{ id: 'autoTranslateResponses', label: 'Auto Translate Responses', key: 'auto_translate_responses', hasSelect: true },
				{ id: 'responseApproval', label: 'Response Approval', key: 'response_approval', hasSelect: false },
			],
			languages: {
				en: __('English'),
				ar: __('Arabic'),
				cs: __('Czech'),
				el: __('Greek'),
				ja: __('Japanese'),
				th: __('Thai'),
				tr: __('Turkish'),
				fr: __('French'),
				he: __('Hebrew'),
				zh: __('Chinese'),

			},
			newConnectionType: 'exclusivi',
			newConnectionId: '',
			teamConnections: [],
			showPmsEmailModal: false,
			showPmsConnectionModal: false,
		};
	},
	mounted() {
		if (!this.partner) {
			this.getTeamConnections();
		}
	},
	watch: {
		team: {
			immediate: true,
			handler: function (newVal, oldVal) {
				this.newNumberOfCompetitors = newVal.max_competitors;
			},
		},
		teamMetrics: {
			immediate: true,
			handler: function (newVal, oldVal) {
				const that = this;
				newVal.features.forEach((feature) => {
					that.loadFeature(feature);
				});
			},
		},
	},
	methods: {
		loadFeature(feature) {
			const featureName = feature.feature;
			if (!allowedFeatures.includes(featureName)) {
				return;
			}
			if (this.features[featureName]) {
				this.features[featureName].enabled = true;
				this.features[featureName].feature_data = feature.feature_data ?? this.features[featureName].feature_data;
				this.features[featureName].created_at = moment(feature.created_at).format('DD/MM/YYYY');
			}
		},

		removeTeamGroup(group) {
			axios
				.delete(`/api/admin/teams/${this.team.id}/remove-group/${group}`)
				.then((response) => {
					this.$emit('update-triggered', true);
				})
				.catch((error) => {
					swal('Whoops!', 'Something went wrong.', 'error');
				});
		},
		editConnection(connection) {
			if (connection.connection_type === 'email') {
				this.showPmsEmailModal = true;
			}
		},
		changePropertyType() {
			const data = {
				type: this.propertyType,
			};
			axios
				.post(`/api/teams/${this.team.id}/change-type`, data)
				.then((response) => {
					this.propertyTypeChanged = false;
				})
				.catch((error) => {
					console.error('Error:', error);
				});
		},
		getTeamConnections() {
			axios
				.get(`/api/admin/teams/${this.team.id}/connections`)
				.then((response) => {
					this.teamConnections = response.data;
					this.propertyType = this.teamMetrics.type;
				})
				.catch((error) => {
					console.error('Error fetching team connections:', error);
				});
		},
		removeConnection(connection) {
			swal(
				{
					title: `Are you sure you want to delete this connection?`,
					text: 'The action will apply immediately!',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Remove!',
				},
				async (result) => {
					if (result) {
						if (connection.connection_type === 'email') {
							SurveysService.pmsSettings.delete('email', this.team.id);
						}
						axios
							.delete(`/api/admin/teams/${connection.id}/delete-connection`)
							.then((response) => {
								this.getTeamConnections();
							});
					}
				},
			);
		},
		addConnection(connection = {}) {
			const shouldSubmit = connection.submit;
			if (this.newConnectionType === 'email') {
				if (shouldSubmit) {
					const postData = {
						type: 'email',
						connectionId: this.newConnectionId,
					};
					axios
						.post(`/api/admin/teams/${this.team.id}/add-connection`, postData)
						.then((response) => {
							this.newConnectionId = '';
							this.getTeamConnections();
							this.showPmsEmailModal = false;
						})
						.catch((error) => console.error('Error adding email connection:', error));
				} else {
					this.showPmsEmailModal = true;
				}
			} else if (this.newConnectionType === 'ftp') {
				this.showPmsConnectionModal = true;
			} else {
				if (!shouldSubmit) {
					const postData = {
						type: this.newConnectionType,
						connectionId: this.newConnectionId,
					};
					axios
						.post(`/api/admin/teams/${this.team.id}/add-connection`, postData)
						.then((response) => {
							this.newConnectionId = '';
							this.getTeamConnections();
						})
						.catch((error) =>
							console.error(
								`Error adding ${this.newConnectionType} connection:`,
								error,
							),
						);
				}
			}
		},

		changeTrialDate(date) {
			this.newTrialDate = date;
		},
		async updateTrial() {
			const postData = {
				new_trial_date: this.newTrialDate,
				new_number_of_competitors: this.newNumberOfCompetitors,
			};
			if (this.changeFeatures) postData.features = this.features;
			this.isSaving = true;
			if (this.propertyTypeChanged) this.changePropertyType();
			const response = await axios.post(this.updateTrialUrl, postData);
			this.isSaving = false;
			this.$emit('update-triggered', true);
		},
		deleteCompetitor(competitor) {
			swal(
				{
					title: `Are you sure you want to remove ${competitor.name} as a competitor?`,
					text: 'The action will apply immediately!',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Remove!',
				},
				async (result) => {
					if (result) {
						axios
							.delete(`/api/admin/competitor/${competitor.id}/delete`)
							.then((response) => {
								this.$emit('update-triggered', true);
								swal('Done!', 'The competitor has been deleted.', 'success');
							})
							.catch((error) => {
								swal('Whoops!', 'Something went wrong.', 'danger');
							});
					}
				},
			);
		},
	},
};
</script>

<style scoped>
.mapping-section {
	margin-bottom: 20px;
}

.team-mappings,
.field-mappings {
	margin-top: 10px;
}

.team-mappings ul,
.field-mappings ul {
	list-style-type: none;
	padding: 0;
}

.scrollable-container {
	max-height: 250px;
	overflow-y: auto;
	overflow-x: hidden;
}

.row {
	display: flex;
	flex-wrap: wrap;
	margin-right: -6px;
	margin-left: -3px;
}

.form-icon {
	position: absolute;
	top: 17px;
	left: 0px;
}

.has-icon form-control {
	padding-left: 40px;
}

.no-border {
	border: none !important;
}
.modal-custom {
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1040; /* Ensure this is below your modal but above other content */
	width: 100vw;
	height: 100vh;
	background-color: #41425ad0; /* Semi-transparent black */
}
</style>
